import React, { useState, useEffect, useCallback } from "react";
import Button from "@/common/ui/Button";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import EmptyList from "@/components/common/EmptyList";
import { Center, Row } from "@/common/css";
import TableView from "@/components/employee/TableView";
import Icon, { Image } from "@/common/ui/Image";
import { getOrgConfig, useOrgEmployee } from "@/services/employee.services";
import { loginSelector } from "@/store/features/loginSlice";
import { useSelector } from "react-redux";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import AddEmployee from "@/components/employee/modal/AddEmployee";
import { RxCross2 } from "react-icons/rx";

const modalConfig: ModalConfig = {
  add: {
    component: AddEmployee,
    title: "Add Employee",
    type: "side",
    showCloseWarning: true,
  },
  edit: {
    component: EditEmployee,
    title: "Edit Employee",
    type: "side",
    isQueryRequired: true,
    showCloseWarning: true,
  },
  "exit-terminate": {
    component: ExitEmployee,
    title: "Exit termination",
    type: "side",
    isQueryRequired: true,
    showCloseWarning: true,
  },
  "manage-column": {
    component: ManageColumn,
    title: "Manage column",
    type: "side",
    isQueryRequired: true,
    showCloseWarning: true,
  },
  "share-link": {
    component: ShareLink,
    title: "Share as a link",
    type: "center",
    isQueryRequired: true,
  },
  "re-invite": {
    component: ReInvite,
    title: "Reinvite",
    type: "center",
    isQueryRequired: true,
  },
  delete: {
    component: DeleteEmployee,
    title: "Delete",
    type: "center",
    isQueryRequired: true,
  },
  importEmployee: {
    component: ImportEmployee,
    title: "Import employee",
    type: "side",
    modalClassName: "max-w-[450px]",
    showCloseWarning: true,
  },
  webaccess: {
    component: WebAccess,
    title: "Web Access",
    type: "center",
    modalClassName: "max-w-[450px]",
    showCloseWarning: true,
  },
};

interface EmployeesPageProps {
  openModal: (modalKey: string, props?: any) => void;
  closeModal: () => void;
  activeModal: string;
}

import { FiSearch, FiFilter, FiChevronDown } from "react-icons/fi";
import Filter from "@/components/employee/modal/Filter";
import { useAppDispatch } from "@/store/store";
import { employeeSelector, setEmployeeData, setSearchQuery, addFilter, setTableConfig } from "@/store/features/employeeSlice";
import EditEmployee from "@/components/employee/modal/EditEmployee";
import ExitEmployee from "@/components/employee/modal/ExitTermination";
import ShareLink from "@/components/employee/modal/ShareLink";
import ReInvite from "@/components/employee/modal/ReInvite";
import { useNavigate, useParams } from "react-router-dom";
import SingleSelectWithCategory from "@/common/custom/SingleSelectWithCategory";
import useClickOutside from "@/hooks/useClickOutSide";
import { PERMISSIONS, PROFILE_STATUS } from "@/utils/constant/global.constant";
import DeleteEmployee from "@/components/employee/modal/DeleteEmployee";
import ImportEmployee from "./modal/ImportEmployee";
import { SetupProfileModal } from "../dashboard/DashboardPage";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import ManageColumn from "@/components/employee/modal/ManageColumn";
import { isHasPermission } from "@/utils/helper/permissions";
import { ActionsDropdown } from "@/common/ActionsButton";
import { LightText2 } from "@/common/ui/Text";
import WebAccess from "./modal/WebAccess";

type ButtonGroupUIProps = {
  onAddClick: () => void;
  onClickFilter: () => void;
  onClickSearch: () => void;
  openModal: (modalKey: string, props?: any) => void;
  showFilter: boolean;
};
interface AccessCounts {
  totalEmps: number;
  noWebaccess: number;
  hasWebaccess: number;
  exEmployes: number;
  disabledWebAccess: number;
  accountOwner: number;
}

const ButtonGroupUI = ({ onAddClick, onClickFilter, onClickSearch, openModal, showFilter }: ButtonGroupUIProps) => {
  const [showAddOptions, setShowAddOptions] = useState(false);
  const { currentUser } = useSelector(loginSelector);
  const { fetchFilters } = useSelector(employeeSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      dispatch(setSearchQuery(searchInput));
      setTimeout(() => onClickSearch(), 200);
    }
  };
  const handleSearch = (q: string) => {
    setSearchInput(q);
    if (!q) {
      dispatch(setSearchQuery(q));
      setTimeout(() => onClickSearch(), 200);
    }
  };
  const addRef = useClickOutside(() => setShowAddOptions(false));
  // const childOptions = [
  //   {
  //     name: "export-history",
  //     label: "Export history",
  //     onClick: () => {},
  //   },
  //   {
  //     name: "recycle-bin",
  //     label: "Recycle bin",
  //     onClick: () => {},
  //   },
  // ];

  useEffect(() => {
    if (!searchInput) {
      dispatch(setSearchQuery(""));
    }
  }, []);

  return (
    <div className="flex items-center justify-between gap-3 w-full">
      <div className="relative m-1">
        <input
          type="text"
          placeholder="Search"
          value={searchInput}
          onChange={(e: any) => {
            let value = e.target.value;
            // Block '%' and length > 50
            if (value.includes("%") || value.length > 50) return;
            // Block initial space
            if (value.length > 0 && value.trim().length === 0) return;
            if (value?.trim().length === 0) {
              value = value.trim();
            }
            handleSearch(value);
          }}
          onKeyDown={handleKeyDown}
          className="pl-3 pr-[54px] py-1.5 md:w-60 rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary placeholder:text-gray-400"
        />
        <Center className="absolute right-3 top-1/2 -translate-y-1/2">
          {searchInput?.length > 0 ? (
            <Center className="gap-1">
              <RxCross2 className="size-5 text-text-default hover:cursor-pointer" onClick={() => handleSearch("")} />
              <Image
                src="/icons/search.svg"
                alt=""
                className=" size-5 hover:cursor-pointer"
                onClick={() => {
                  dispatch(setSearchQuery(searchInput));
                  setTimeout(() => onClickSearch(), 200);
                }}
              />
            </Center>
          ) : (
            <FiSearch className=" text-text-primary" />
          )}
        </Center>
      </div>
      <div className="flex items-center gap-2 overflow-visible">
        <div className="relative flex shadow-sm rounded-md z-50" ref={addRef}>
          <button
            className="px-4 py-7 bg-text-primary text-white text-13 !leading-normal font-medium whitespace-nowrap rounded-l-md transition-all active:bg-brand-dark hover:bg-brand-secondary active:ring active:ring-brand-shade active:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onAddClick()}
            disabled={isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Create Employees"], { isDisableCheck: true })}
          >
            <span className="hidden md:inline">Add Employee</span>
            <span className="md:hidden">Add</span>
          </button>
          <button
            className="px-2 bg-text-primary text-white text-13 !leading-normal hover:bg-text-primary border-l-[1px] rounded-r-md"
            onClick={() => setShowAddOptions((prev) => !prev)}
          >
            <FiChevronDown size={16} />
          </button>

          {showAddOptions && (
            <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-200 shadow-xl rounded-md z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
              <button
                className="px-4 w-full text-start py-2 hover:bg-gray-100 cursor-pointer text-13 !leading-normal text-gray-700 transition-colors duration-150 first:rounded-t-md disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setShowAddOptions(false);
                  openModal("importEmployee");
                }}
                disabled={isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Import Employees"], { isDisableCheck: true })}
              >
                Import Employees
              </button>
              <div
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-13 !leading-normal text-gray-700 transition-colors duration-150 last:rounded-b-md"
                onClick={() => {
                  setShowAddOptions(false);
                  navigate("/home/employees/import-history");
                }}
              >
                Import History
              </div>
            </div>
          )}
        </div>
        <button
          className={`flex items-center gap-1 px-4 pr-6 py-7 rounded-md text-13 !leading-normal relative whitespace-nowrap ${
            showFilter ? "bg-purple-100 text-purple-700" : "bg-purple-50 text-purple-600 hover:bg-purple-100"
          }`}
          onClick={onClickFilter}
        >
          <FiFilter size={16} />
          {fetchFilters?.length > 0 && (
            <>
              <div className="absolute top-1/2 right-1 -translate-y-1/2 bg-text-primary size-4 rounded-full flex items-center justify-center">
                <h2 className=" text-white text-13 !leading-normal">{fetchFilters.length}</h2>
              </div>
              <div className=" size-2.5 rounded-full bg-text-error absolute top-[20%] left-6 "></div>
            </>
          )}
          <span className="hidden md:block">Filter</span>
        </button>
      </div>
      {/* <div className="relative">
        <ActionsDropdown childOptions={childOptions} dropdownClassName="top-12 right-0" listClassName="hover:text-text-primary hover:bg-primaryOpacity-10" />
      </div> */}
    </div>
  );
};

const options = {
  Default: [
    { label: "All Employees", value: "all-employees" },
    { label: "Active Employees", value: "active-employees" },
    { label: "Ex-Employees", value: "ex-employees" },
  ],
};

const MESSAGES: any = {
  common: "No employees found!",
  "all-employees": "Oops! You don’t have any employees yet.",
  "active-employees": "No active employees found at the moment.",
  "ex-employees": "No former employees have been added yet.",
};

const EmployeesPage: React.FC<EmployeesPageProps> = ({ openModal, closeModal, activeModal: _activeModal }) => {
  const { currentOrg, currentUser } = useSelector(loginSelector);
  const { employeesData, tableConfig } = useSelector(employeeSelector);
  const pageEvent = useSelector(pageEventSelector);
  const dispatch = useAppDispatch();
  const { searchQuery: query, pagination, fetchFilters, sortBy } = useSelector(employeeSelector);
  const [rowSelection, setRowSelection] = useState({});
  const navigate = useNavigate();
  const { employeeId, modalName } = useParams();
  const [showFilter, setShowFilter] = useState(false);
  
  // Handle close for share-link modal with navigation
  const handleShareLinkClose = useCallback(() => {
    navigate("/home/employees", { replace: true });
    closeModal();
  }, [navigate, closeModal]);

  // Local pagination state to persist count during API calls
  const [localPagination, setLocalPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 20,
  });

  useEffect(() => {
    if (!modalName) return;

    const modalExist = modalConfig[modalName];

    if (!modalExist) {
      navigate("/home/employees", { replace: true });
      return;
    }

    const requiresId = modalExist.isQueryRequired;

    if (requiresId && !employeeId) {
      navigate("/home/employees", { replace: true });
      return;
    }

    // Pass custom closeModal for share-link modal
    let modalProps: any = requiresId ? { employeeId } : undefined;
    if (modalName === "share-link" && modalProps) {
      modalProps.closeModal = handleShareLinkClose;
    }

    openModal(modalName, modalProps);
  }, [modalName, employeeId, location.pathname, location.search, handleShareLinkClose, openModal]);


  useEffect(() => {
    refetch();
  }, [sortBy, pagination.pageSize]);

  const shouldFetch = currentOrg?.SignupOrg?.acc_sts_id === PROFILE_STATUS["PRFL-CMTD"];
  const [views, setViews] = useState({ label: "All Employees", value: "all-employees" });
  const {
    data: employees,
    isLoading,
    refetch,
    isFetching,
  } = useOrgEmployee(currentOrg, {
    searchQuery: query?.trim(),
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    filters: fetchFilters,
    sortBy: sortBy.length > 0 ? sortBy : [{ field: "empId", isASC: true }],
    enabled: shouldFetch,
    views,
  });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [accessCounts, setAccessCounts] = useState<AccessCounts>({
    totalEmps: 0,
    noWebaccess: 0,
    hasWebaccess: 0,
    disabledWebAccess: 0,
    accountOwner: 0,
    exEmployes: 0,
  });

  const handleOpenAddEmpModal = () => {
    openModal("add", { afterCall: refetch });
    navigate("/home/employees/action/add?", { replace: true });
  };
  const handleViewChange = (view: any) => {
    setViews(view);
    setTimeout(() => refetch(), 200);
  };

  const childOptions = [
    {
      name: "enable web access",
      label: "Enable web access",
      onClick: () => {
        openModal("webaccess", {
          data: selectedRows,
          setRowSelection: setRowSelection,
          afterCall: () => refetch(),
          accessCounts,
          mode: "grant",
          selectionType: "multi",
          employeesData,
        });
      },
    },
    {
      name: "disable web access",
      label: "Disable web access",
      onClick: () => {
        openModal("webaccess", {
          data: selectedRows,
          setRowSelection: setRowSelection,
          afterCall: () => refetch(),
          accessCounts,
          mode: "revoke",
          selectionType: "multi",
          employeesData,
        });
      },
    },
  ];

  const fetchTableConfig = async () => {
    try {
      const res: any = await getOrgConfig("emp_tbl");
      if (res?.status === 200 && res.data) {
        dispatch(setTableConfig(res.data?.data));
      }
    } catch (error) {
      console.error("Failed to fetch table config:", error);
    }
  };

  useEffect(() => {
    if (tableConfig === null) {
      fetchTableConfig();
    }
  }, [tableConfig]);

  useEffect(() => {
    if (employees?.rows) {
      dispatch(setEmployeeData(employees.rows));
    }
  }, [employees, dispatch]);

  // Update local pagination when API response changes
  useEffect(() => {
    if (employees?.pagination) {
      setLocalPagination({
        total: employees.pagination.total || 0,
        totalPages: employees.pagination.totalPages || 0,
        currentPage: employees.pagination.currentPage || pagination.pageNumber,
        pageSize: employees.pagination.pageSize || pagination.pageSize,
      });
    }
  }, [employees?.pagination, pagination.pageNumber, pagination.pageSize]);

  useEffect(() => {
    if (pageEvent?.keys.includes("employeeData") && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent]);

  return (
    <div className="flex flex-col flex-1 h-full w-full">
      <SideBarContentHeader className="!pb-3.5 !pt-0 px-4">
        {Object.keys(rowSelection).length > 0 ? (
          <div className="flex items-center gap-2 pt-2">
            {/* <Button variant="primary" size="small" onClick={() => {}}>
              Export
            </Button> */}

            {isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Web Access"]) && (
              <>
                <div className="relative">
                  <ActionsDropdown
                    text="Bulk action"
                    childOptions={childOptions}
                    dropdownClassName="top-12 left-0"
                    listClassName="hover:text-text-primary hover:bg-primaryOpacity-10"
                  />
                </div>
                <Button variant="outline" size="small" className="" onClick={() => setRowSelection({})}>
                  Cancel
                </Button>
              </>
            )}

            <LightText2 text={`${Object.keys(rowSelection).length} employee(s) selected`} />
          </div>
        ) : (
          <div className="flex justify-between w-full items-center">
            <div className="flex items-center">
              <SingleSelectWithCategory
                options={options}
                onChange={handleViewChange}
                selectedValue={views}
                className="text-base font-bold text-text-default"
                dropdownClassName="text-13 z-50"
                listClassName="text-text-default"
                textClassname="text-text-default"
                count={localPagination.total}
              />
              <Button variant="transparent" size="small" className="!p-0 !min-w-0" onClick={() => refetch()}>
                <Icon src="/icons/refresh.svg" alt="refresh icon" className={`${isLoading || isFetching ? "animate-spin-reverse" : ""} `} />
              </Button>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <ButtonGroupUI
                onAddClick={handleOpenAddEmpModal}
                onClickFilter={() => {
                  setShowFilter(!showFilter);
                  if (!showFilter) {
                    dispatch(addFilter());
                  }
                }}
                onClickSearch={() => refetch()}
                openModal={openModal}
                showFilter={showFilter}
              />
            </div>
          </div>
        )}
        {/* <div className="flex flex-col md:flex-row md:justify-between w-full items-start md:items-center gap-4 md:gap-0">
          <div className="flex items-center w-full md:w-auto">
            <SingleSelectWithCategory
              options={options}
              onChange={handleViewChange}
              selectedValue={views}
              className="text-base font-bold text-text-default"
              dropdownClassName="text-13 z-50"
              listClassName="text-text-default"
              textClassname="text-text-default"
              count={localPagination.total}
            />
            <Button variant="transparent" size="small" className="!p-0 !min-w-0" onClick={() => refetch()}>
              <Icon src="/icons/refresh.svg" alt="refresh icon" className={`${isLoading || isFetching ? "animate-spin-reverse" : ""} `} />
            </Button>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <ButtonGroupUI
              onAddClick={handleOpenAddEmpModal}
              onClickFilter={() => openModal("filter", {})}
              onClickSearch={() => refetch()}
              openModal={openModal}
            />
          </div>
        </div> */}
      </SideBarContentHeader>
      <div className="flex flex-row gap-2" style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}>
        <div className={`flex flex-col ${showFilter ? "flex-1" : "w-full"} min-w-0`}>
          {/* {employeesData && employeesData?.length > 0 && query.length > 0 && (
            <div className=" flex items-center gap-2">
              <LightText2 text={`Search result for`} className=" !text-10" />
              <span className=" text-text-primary font-semibold !text-12">{`"${query}"`}</span>
              <button onClick={() => setQuery("")} className=" text-rose-500 !text-10 hover:text-rose-700">
                Clear result
              </button>
            </div>
          )} */}
          {/* Table View */}
        {!isFetching && employeesData?.length === 0 ? (
          <>
            {query ? (
              <EmptyList text="No employees found." icon="/icons/empty-list-file.svg" className=" h-full gap-y-4"></EmptyList>
            ) : (
              <EmptyList
                text={fetchFilters.length > 0 || query?.length > 0 ? MESSAGES["common"] : MESSAGES[views.value]}
                icon="/icons/empty-list-file.svg"
                className=" h-full gap-y-4"
              >
                <Row className=" flex items-center gap-4">
                  {views.value === "all-employees" &&
                    currentOrg?.SignupOrg?.acc_sts_id === PROFILE_STATUS["PRFL-CMTD"] &&
                    !(fetchFilters.length > 0 || query?.length > 0) && (
                      <Button variant="primary" size="small" onClick={handleOpenAddEmpModal}>
                        Add
                      </Button>
                    )}
                  {/* <Button variant="primary" size="small" onClick={handleOpenAddEmpModal} leftIcon={<PlusCircle className=" size-4" />}>
                  Import
                </Button> */}
                </Row>
              </EmptyList>
            )}
          </>
        ) : (
          <TableView
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            className=" flex flex-col justify-between h-full"
            employees={employeesData && employeesData?.length > 0 ? employeesData : []}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            openModal={openModal}
            handleShareLinkClose={handleShareLinkClose}
            count={localPagination.total}
            onClickFetch={() => refetch()}
            loading={isFetching || isLoading}
            tableConfig={tableConfig}
            setAccessCounts={setAccessCounts}
          />
        )}
        </div>
        {showFilter && (
          <>
            <div className="hidden md:block w-[300px] flex-shrink-0" style={{ position: 'relative', display: 'block' }}>
              <div className="bg-white rounded-lg shadow-lg flex flex-col" style={{ height: '100%', maxHeight: 'calc(100vh - 200px)', display: 'flex' }}>
                {/* <div className="flex justify-between items-center p-4 pb-2 border-b-2 border-gray-300 flex-shrink-0 bg-gray-50">
                  <h2 className="text-lg font-semibold text-text-default">Filter</h2>
                </div> */}
                <div className="flex-1 overflow-y-auto bg-white" style={{ minHeight: 0 }}>
                  <Filter showAddFilterButton={true} onClose={() => setShowFilter(false)} />
                </div>
              </div>
            </div>
            {/* <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowFilter(false)}>
              <div className="absolute right-0 top-0 h-full w-[300px] bg-white shadow-lg overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center p-4 pb-2 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-text-default">Filter</h2>
                  <button onClick={() => setShowFilter(false)} className="text-gray-500 hover:text-gray-700">
                    <RxCross2 size={24} />
                  </button>
                </div>
                <Filter showAddFilterButton={true} />
              </div>
            </div> */}
          </>
        )}
      </div>
      {currentOrg?.SignupOrg?.acc_sts_id !== PROFILE_STATUS["PRFL-CMTD"] && (
        <SetupProfileModal onVerify={() => navigate("/home/settings/organization-profile")} />
      )}
    </div>
  );
};

export const EmployeesPageWithModal = withModal(EmployeesPage, modalConfig);
